/**
 * Public Trade Association Profile — /associations/:id
 *
 * NO auth. Anyone can see it. Big CTA deep-links into Register with
 * the association pre-selected, or ID Card page if already signed in.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import BrandName from "@/components/BrandName";
import UnionLogo from "@/components/UnionLogo";
import LogoUploadPanel from "@/components/LogoUploadPanel";
import { AnnouncementWall, CouncilRoster } from "@/components/SocialFeed";
import {
  ShieldCheck, MapPin, ArrowLeft, ExternalLink, BadgeCheck,
  Users, Sparkles, ChevronRight, Info, BarChart3, Mail, Phone,
  Landmark, User,
} from "lucide-react";

const INDUSTRY_LABELS = {
  bollywood: "Bollywood", tamil: "Tamil Cinema", telugu: "Telugu Cinema",
  malayalam: "Malayalam Cinema", kannada: "Kannada Cinema",
  marathi: "Marathi Cinema", bengali: "Bengali Cinema",
  gujarati: "Gujarati Cinema", punjabi: "Punjabi Cinema",
  bhojpuri: "Bhojpuri Cinema", odia: "Odia Cinema",
  assamese: "Assamese Cinema", all_india: "All-India Body",
  tv: "Television", ott: "OTT / Digital",
  performing_arts: "Performing Arts · Theatre",
};

const ROLE_LABELS = {
  lighting: "Lighting Technicians", grip: "Grip Crew",
  sound: "Sound / Location Sound", makeup: "Make-up · Hair",
  spot_boy: "Spot Boys · Attendants", camera: "Camera Assistants",
  dop: "Cinematographers", director: "Directors", ad: "Assistant Directors",
  producer: "Producers", editor: "Editors", vfx: "VFX Artists",
  costume: "Costume Designers", stunt: "Stunt Performers",
  choreographer: "Choreographers", main_talent: "Actors · Main Talent",
  music: "Music Composers · Musicians",
};

export default function AssociationProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [p, setP]           = useState(null);
  const [loading, setL]     = useState(true);
  const [notFound, setNF]   = useState(false);
  const [canManage, setCM]  = useState(false);
  const [myClaim, setMC]    = useState(null);

  useEffect(() => {
    setL(true); setNF(false); setCM(false); setMC(null);
    reloadProfile();
    // Access probe — only meaningful for signed-in users; anonymous
    // callers hit 401/403 which we silently ignore.
    if (user) {
      api.get(`/associations/${id}/analytics/access`)
        .then(({ data }) => setCM(!!data.can_manage))
        .catch(() => setCM(false));
      api.get(`/union-claims/mine/for/${id}`)
        .then(({ data }) => setMC(data.claim))
        .catch(() => setMC(null));
    }
  }, [id, user]);

  function reloadProfile() {
    // Fetch (or refetch) the profile — used after a logo upload so
    // the hero + card + monogram fallback all update instantly.
    // Append a cache-buster to bypass axios's default caching.
    api.get(`/public/associations/${id}?t=${Date.now()}`)
      .then(({ data }) => setP(data))
      .catch(() => setNF(true))
      .finally(() => setL(false));
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center text-white/40 uppercase tracking-[0.3em] text-xs">
        Loading profile…
      </div>
    );
  }
  if (notFound || !p) {
    return (
      <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center flex-col gap-3">
        <div className="text-white/60">This association isn&apos;t on Call·Sheet yet.</div>
        <Link to="/associations" className="text-[10px] uppercase tracking-[0.28em] text-fuchsia-400"
          data-testid="profile-back-to-dir">← Back to directory</Link>
      </div>
    );
  }

  const alreadyMember = user && user.association_id === p.id;
  const claimHref     = user
    ? "/icard"
    : `/register?association_id=${encodeURIComponent(p.id)}`;

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-white" data-testid="association-profile">
      {/* Top strip */}
      <div className="border-b border-white/5 bg-gradient-to-b from-[#151519] to-[#0a0a0c]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <Link to="/" className="text-xl"><BrandName /></Link>
          <div className="flex items-center gap-4">
            {canManage && (
              <Link to={`/associations/${id}/analytics`}
                className="text-[10px] uppercase tracking-[0.24em] px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5"
                style={{ borderColor: `${p.color}66`, color: p.color, background: `${p.color}12` }}
                data-testid="profile-open-analytics">
                <BarChart3 size={11} /> Open Analytics
              </Link>
            )}
            {canManage && (
              <Link to="/payout-account"
                className="text-[10px] uppercase tracking-[0.24em] px-3 py-1.5 rounded-full border border-emerald-400/40 text-emerald-300 bg-emerald-400/10 hover:bg-emerald-400/20 inline-flex items-center gap-1.5"
                data-testid="profile-open-payout">
                <Landmark size={11} /> Payout & KYC
              </Link>
            )}
            <Link to="/associations"
              className="text-[10px] uppercase tracking-[0.28em] text-white/60 hover:text-white inline-flex items-center gap-1.5"
              data-testid="profile-back-to-dir">
              <ArrowLeft size={11} /> Directory
            </Link>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-10">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="flex items-start gap-5 sm:gap-6 flex-wrap">
              <UnionLogo assoc={p} size={80} rounded="xl" className="shadow-lg" />
              <div className="min-w-0 flex-1">
                <div className="text-[10px] tracking-[0.32em] uppercase" style={{ color: p.color }}>
                  {INDUSTRY_LABELS[p.industry] || p.industry} · {p.city}
                </div>
                <h1 className="mt-2 font-display text-4xl sm:text-5xl uppercase tracking-tight leading-[0.95]"
                  style={{ color: p.color }} data-testid="profile-name">
                  {p.name}
                </h1>
                <div className="mt-3 text-white/85 text-lg" data-testid="profile-full">
                  {p.full}
                </div>
              </div>
            </div>
            {p.scope && (
              <p className="mt-6 text-white/60 text-sm sm:text-base leading-relaxed max-w-2xl"
                data-testid="profile-scope">
                {p.scope}
              </p>
            )}
            {p.contact?.website && (
              <a href={p.contact.website} target="_blank" rel="noreferrer"
                className="mt-4 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-white/60 hover:text-white"
                data-testid="profile-website">
                {p.contact.website.replace(/^https?:\/\//, "")} <ExternalLink size={10} />
              </a>
            )}
            {(p.contact?.email || p.contact?.phone) && (
              <div className="mt-2 flex items-center gap-4 text-[11px] text-white/60 flex-wrap">
                {p.contact.email && (
                  <a href={`mailto:${p.contact.email}`}
                    className="inline-flex items-center gap-1.5 hover:text-white"
                    data-testid="profile-email">
                    <Mail size={11} /> {p.contact.email}
                  </a>
                )}
                {p.contact.phone && (
                  <a href={`tel:${p.contact.phone.replace(/\s+/g, "")}`}
                    className="inline-flex items-center gap-1.5 hover:text-white"
                    data-testid="profile-phone">
                    <Phone size={11} /> {p.contact.phone}
                  </a>
                )}
              </div>
            )}
            {(p.contact?.address || p.contact?.contact_person) && (
              <div className="mt-3 text-[11px] text-white/55 leading-relaxed max-w-xl space-y-1"
                   data-testid="profile-address-block">
                {p.contact.contact_person && (
                  <div className="flex items-start gap-2" data-testid="profile-contact-person">
                    <User size={11} className="mt-0.5 shrink-0 opacity-70" />
                    <span>{p.contact.contact_person}</span>
                  </div>
                )}
                {p.contact.address && (
                  <div className="flex items-start gap-2" data-testid="profile-address">
                    <MapPin size={11} className="mt-0.5 shrink-0 opacity-70" />
                    <span>{p.contact.address}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Claim CTA card */}
          <div className="lg:col-span-1">
            <div className="rounded-2xl border p-6"
              style={{ borderColor: `${p.color}44`, background: `${p.color}0f` }}
              data-testid="profile-claim-card">
              <div className="text-[10px] uppercase tracking-[0.28em]" style={{ color: p.color }}>
                Digital Membership
              </div>
              <div className="mt-2 font-display text-xl uppercase tracking-tight leading-tight">
                {alreadyMember ? "You're verified with " + p.name : "Claim your Call·Sheet card"}
              </div>
              <p className="mt-3 text-xs text-white/65 leading-relaxed">
                {alreadyMember
                  ? "Your Call·Sheet Digital ID already carries your " + p.name + " membership. Attendance, procurement and iCard are all unlocked."
                  : "One-time verification with your union card number. Your Call·Sheet Digital ID travels with you across every project."}
              </p>

              {alreadyMember ? (
                <Link to="/icard"
                  className="mt-5 inline-flex items-center justify-center gap-2 w-full px-4 py-3 rounded-full text-black font-semibold text-[11px] uppercase tracking-[0.24em]"
                  style={{ background: p.color }}
                  data-testid="profile-open-icard">
                  <BadgeCheck size={13} /> Open my iCard
                </Link>
              ) : (
                <Link to={claimHref}
                  className="mt-5 inline-flex items-center justify-center gap-2 w-full px-4 py-3 rounded-full text-black font-semibold text-[11px] uppercase tracking-[0.24em]"
                  style={{ background: p.color }}
                  data-testid="profile-claim-cta">
                  Claim my card <ChevronRight size={13} />
                </Link>
              )}
              <div className="mt-3 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-white/45">
                <ShieldCheck size={10} /> Verified · Free forever for members
              </div>
            </div>

            {/* Live stats */}
            {p.stats?.total_members > 0 && (
              <div className="mt-4 rounded-2xl border border-white/10 p-5" data-testid="profile-stats-card">
                <div className="text-[10px] uppercase tracking-[0.28em] text-white/45 flex items-center gap-1.5">
                  <Users size={11} /> Members already on Call·Sheet
                </div>
                <div className="mt-2 font-display text-4xl tabular-nums" style={{ color: p.color }}
                  data-testid="profile-member-count">
                  {p.stats.total_members.toLocaleString()}
                </div>
                {p.stats.role_breakdown?.length > 0 && (
                  <ul className="mt-3 space-y-1.5">
                    {p.stats.role_breakdown.map(r => (
                      <li key={r.role} className="flex items-center justify-between text-[11px]">
                        <span className="text-white/70">{ROLE_LABELS[r.role] || r.role}</span>
                        <span className="text-white/50 tabular-nums">{r.count}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Manage panel — visible only to admins + this union's owner. */}
      {canManage && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
          <LogoUploadPanel assoc={p} onSaved={reloadProfile} />
        </div>
      )}

      {/* Social layer — Announcements + Council roster */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnnouncementWall
          isAdmin={canManage}
          t={{
            bg: "#0a0a0c",
            surface: "#151519",
            ink: "#F5F1E8",
            muted: "rgba(255,255,255,0.5)",
            line: "rgba(255,255,255,0.08)",
            chipBg: "rgba(255,255,255,0.04)",
            accent: p.color || "#D9383A",
            ctaText: "#0a0a0c",
            inputBg: "rgba(255,255,255,0.03)",
          }}
          listUrl={`/public/associations/${p.id}/announcements`}
          createUrl={`/associations/${p.id}/announcements`}
          deleteUrl={(aid) => `/associations/${p.id}/announcements/${aid}`}
          reactUrl={(aid) => `/associations/announcements/${aid}/reactions`}
          photoUrlFor={(post) => post.photo_url
            ? `${API.replace(/\/api$/, "")}${post.photo_url}`
            : ""}
          title="Notices & Bulletins"
          eyebrow="From the Secretariat"
          emptyLabel={canManage
            ? "Post an AGM notice, card-renewal window, or member advisory — it'll show up here."
            : "No notices posted yet."}
          testidPrefix="union-wall"
        />

        <CouncilRoster
          isAdmin={canManage}
          t={{
            bg: "#0a0a0c",
            surface: "#151519",
            ink: "#F5F1E8",
            muted: "rgba(255,255,255,0.5)",
            line: "rgba(255,255,255,0.08)",
            chipBg: "rgba(255,255,255,0.04)",
            accent: p.color || "#D9383A",
            ctaText: "#0a0a0c",
            inputBg: "rgba(255,255,255,0.03)",
          }}
          listUrl={`/public/associations/${p.id}/council`}
          createUrl={`/associations/${p.id}/council`}
          deleteUrl={(cid) => `/associations/${p.id}/council/${cid}`}
          photoUrlFor={(m) => m.photo_url
            ? `${API.replace(/\/api$/, "")}${m.photo_url}`
            : ""}
          title="Office Bearers"
          eyebrow="The Council"
          testidPrefix="union-council"
        />
      </div>

      {/* Card benefits */}
      {(p.allowed_roles?.length > 0 || p.profile_fields?.length > 0) && (
        <div className="border-t border-white/5">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
            <div className="text-[10px] uppercase tracking-[0.32em] text-white/45">
              What your Digital ID unlocks
            </div>
            <div className="grid md:grid-cols-2 gap-6 mt-6">
              {p.allowed_roles?.length > 0 && (
                <div className="rounded-2xl border border-white/10 p-6">
                  <div className="text-[10px] uppercase tracking-[0.24em] text-white/55">Roles recognised on this card</div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {p.allowed_roles.map(r => (
                      <span key={r}
                        className="px-3 py-1.5 rounded-full border text-[10px] uppercase tracking-[0.2em]"
                        style={{ borderColor: `${p.color}55`, color: p.color, background: `${p.color}15` }}>
                        {ROLE_LABELS[r] || r}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {p.profile_fields?.length > 0 && (
                <div className="rounded-2xl border border-white/10 p-6">
                  <div className="text-[10px] uppercase tracking-[0.24em] text-white/55">Fields printed on your card</div>
                  <ul className="mt-4 space-y-2">
                    {p.profile_fields.map(f => (
                      <li key={f} className="text-sm text-white/80 flex items-center gap-2">
                        <BadgeCheck size={12} style={{ color: p.color }} />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Missing-contact + secretariat claim nudge */}
      <div className="border-t border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center space-y-3">
          {/* Secretariat claim nudge — only when not already claimed by user, not already an admin */}
          {user && !canManage && !myClaim && (
            <div data-testid="profile-claim-nudge">
              <div className="text-sm text-white/70">
                Are you the {p.name} secretariat?
              </div>
              <Link to={`/associations/${p.id}/claim`}
                className="mt-2 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.24em] px-4 py-2 rounded-full"
                style={{ background: p.color, color: "#000" }}
                data-testid="profile-claim-cta-secretariat">
                <ShieldCheck size={11} /> Claim this union
              </Link>
            </div>
          )}
          {/* In-flight claim by this user */}
          {myClaim && ["pending", "info_requested"].includes(myClaim.status) && (
            <div data-testid="profile-my-claim-status">
              <span className="text-[11px] uppercase tracking-[0.22em] px-3 py-1.5 rounded-full border border-amber-400/40 text-amber-300 bg-amber-400/10">
                Your claim is {myClaim.status.replace("_", " ")}
              </span>
              <Link to="/my-claims" className="ml-3 text-[11px] text-fuchsia-400 hover:text-fuchsia-300 uppercase tracking-[0.22em]">
                Track →
              </Link>
            </div>
          )}
          {/* Legacy: missing contact — visible to anyone */}
          {!p.contact?.website && !p.contact?.email && !p.contact?.phone && (
            <div className="text-[11px] text-white/45 uppercase tracking-[0.22em]" data-testid="profile-missing-contact">
              <Info size={11} className="inline mr-1" />
              Prospective admin? <a href="mailto:onboarding@callsheet.app" className="text-fuchsia-400 hover:text-fuchsia-300">Write to onboarding@callsheet.app</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
